import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { ArrowRight } from "lucide-react";
import { Img, SIZES } from "@/components/Img";
import { type Locale } from "@/i18n/config";
import { pathFor, type PageId } from "@/i18n/slugMap";
import { BASE_URL, pageHead } from "@/i18n/head";
import { serviceSchema } from "@/lib/schema";
import { ydelserStrings } from "@/i18n/content/ydelser";
import kontorGruppe42 from "@/assets/kontor-gruppe-4-2.jpg";
import kontorGruppe1 from "@/assets/kontor-gruppe-1.jpg";
import tavle22 from "@/assets/tavle2-2.jpg";
import julieKontor3 from "@/assets/julie-kontor3.jpg";

/** <head> for the services overview page: pageHead + Service schema. */
export function ydelserHead(locale: Locale) {
  const s = ydelserStrings[locale];
  return pageHead("ydelser", locale, s.meta, {
    breadcrumbName: s.hero.title,
    schema: [
      serviceSchema({
        name: s.hero.title,
        serviceType: s.jsonLd.serviceType,
        description: s.meta.description,
        url: BASE_URL + pathFor("ydelser", locale),
        image: s.meta.ogImage,
      }),
    ],
  });
}

// Card images aligned by index with ydelserStrings.cards.
const cardImages: string[] = [
  kontorGruppe42,
  "/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
  "/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg",
  kontorGruppe1,
  tavle22,
  "/wp-content/uploads/MISupportIT_NTK3754-scaled.jpg",
  julieKontor3,
  "/wp-content/uploads/Dan-Container4_1400x1400-1.jpg",
  "/assets-photos/digitale-vaerktoejer.jpg",
];

// Card link targets aligned by index with ydelserStrings.cards. An internal
// page-id links via pathFor(); an external href opens in a new tab.
type CardLink = { id: PageId } | { external: string };
const cardLinks: CardLink[] = [
  { id: "projektledelse" },
  { id: "byggeledelse" },
  { id: "bygherreraadgivning" },
  { id: "hse" },
  { id: "tidsplanlaegning" },
  { id: "projektoekonomi" },
  { id: "projektassistance" },
  { id: "shutdown" },
  { external: "https://sitesupport.dk/" },
];

export function YdelserPage({ locale }: { locale: Locale }) {
  const t = ydelserStrings[locale];

  return (
    <SiteLayout>
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <p className="text-sm uppercase tracking-widest text-foreground/70">{t.hero.eyebrow}</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight">{t.hero.title}</h1>
          <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.hero.body}</p>
        </div>
      </section>

      <section className="py-24 bg-section-light">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center text-section-light-foreground">{t.section.heading}</h2>
          <p className="mt-6 text-center text-section-light-foreground/80 max-w-3xl mx-auto leading-relaxed">
            {t.section.intro}
          </p>
          <div className="mt-12 grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {t.cards.map((s, i) => {
              const link = cardLinks[i];
              return (
                <article
                  key={s.title}
                  className="group flex flex-col bg-secondary rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/40 hover:-translate-y-1"
                >
                  <div className="relative shrink-0 overflow-hidden">
                    <Img
                      src={cardImages[i]}
                      alt={s.title}
                      sizes={SIZES.card3}
                      className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent" />
                  </div>
                  <div className="p-5 flex flex-col justify-between flex-1 min-w-0">
                    <div>
                      <h2 className="text-foreground font-bold text-lg mb-2 group-hover:text-primary transition-colors">
                        {s.title}
                      </h2>
                      <p className="text-foreground/80 text-sm leading-relaxed line-clamp-3">
                        {s.text}
                      </p>
                    </div>
                    {"external" in link ? (
                      <a
                        href={link.external}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center text-foreground/60 text-xs font-semibold uppercase tracking-wider group-hover:text-foreground transition-colors"
                      >
                        {t.readMore}
                        <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </a>
                    ) : (
                      <Link
                        to={pathFor(link.id, locale)}
                        className="mt-4 inline-flex items-center text-foreground/60 text-xs font-semibold uppercase tracking-wider group-hover:text-foreground transition-colors"
                      >
                        {t.readMore}
                        <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.cta.heading}</h2>
          <p className="mt-6 text-lg leading-relaxed">{t.cta.body}</p>
          <Link to={pathFor("kontakt", locale)} className="mt-10 btn-cta">
            {t.cta.button} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
